import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import M from 'materialize-css';
import queryHooks from '../common/queryHooks/index.ts';
import useActingAsId from '../hooks/useActingAsId.ts';
import queryClient from '../utilities/queryClient.ts';
import { USER_QUERY_KEY } from '../common/queryHooks/userQueryHooks.ts';
import endpoints from '../common/endpoints.ts';

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

type TAutoCompleteData = { [key: string]: null };

const ActAsUserModal = ({ isOpen, setIsOpen }: IProps) => {
  const { setActingAsId } = useActingAsId();

  const modal = useRef<HTMLDivElement>(null);
  const autoComplete = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState<string>('');
  const { data } = queryHooks.tools.useUserReadAll();

  const getModal = useCallback(
    () => (modal.current ? M.Modal.getInstance(modal.current) : null),
    [modal]
  );

  const open = useCallback(() => getModal()?.open(), [getModal]);
  const close = useCallback(() => getModal()?.close(), [getModal]);

  useEffect(() => {
    const { current } = modal;
    if (!current) return;

    M.Modal.init(current, {
      onOpenEnd: M.updateTextFields,
      onCloseEnd: () => setIsOpen(false),
    });

    return () => getModal()?.destroy();
  }, [modal.current, getModal]);

  useEffect(() => {
    isOpen ? open() : close();
  }, [getModal, open, close, isOpen]);

  useEffect(() => {
    if (!data) return;
    const { _acting_as_id } = JSON.parse(localStorage.getItem('auth_data')!);
    if (!_acting_as_id) return;

    const user = data.find(({ id }) => id === _acting_as_id);
    if (!user) return;

    const { firstName, lastName, customerName, emailAddress } = user;
    const label = `${customerName}: ${firstName} ${lastName} - ${emailAddress}`;
    setSearch(label);
  }, [data, isOpen]);

  const setUser = useCallback(
    (text: string) => {
      if (!data?.length) return;

      const user = data.find(
        ({ firstName, lastName, customerName, emailAddress }) => {
          const label = `${customerName}: ${firstName} ${lastName} - ${emailAddress}`;
          return text === label;
        }
      );

      if (!user) return;
      setActingAsId(user.id);
      queryClient.invalidateQueries({
        queryKey: [USER_QUERY_KEY, endpoints.user.read.one.by.id()],
      });
      queryClient.invalidateQueries({
        queryKey: [
          USER_QUERY_KEY,
          endpoints.fastspring.read.all.subscriptions.by.userId(),
        ],
      });
      close();
    },
    [data]
  );

  useEffect(() => {
    const { current } = autoComplete;
    if (!current) return;
    const obj: TAutoCompleteData = {};
    data?.forEach(({ customerName, firstName, lastName, emailAddress }) => {
      const label = `${customerName}: ${firstName} ${lastName} - ${emailAddress}`;
      obj[label] = null;
    });

    M.Autocomplete.init(current, {
      data: obj,
      onAutocomplete: setUser,
    });
    return () => M.Autocomplete.getInstance(current)?.destroy();
  }, [data]);

  return (
    <div ref={modal} className="modal">
      <div className="modal-content">
        <div className="row">
          <h5 className="center">Act as Another User</h5>
        </div>
        <div className="row">
          <div className="input-field col s12">
            <input
              type="text"
              id="userSearch"
              ref={autoComplete}
              className="autocomplete"
              value={search}
              onChange={({ target: { value } }) => setSearch(value)}
            />
            <label htmlFor="userSearch">Search Users</label>
          </div>
        </div>
        <p style={{ height: '300px' }}>&nbsp;</p>
      </div>
    </div>
  );
};

export default ActAsUserModal;
